export interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'agent';
  profile: {
    displayName: string;
    avatarUrl?: string | undefined;
    styleProfile?: string | undefined;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MediaReference {
  providerMediaId: string;
  type: 'audio' | 'document' | 'image' | 'sticker' | 'video' | 'unknown';
  mimeType?: string | undefined;
  filename?: string | undefined;
  localPath?: string | undefined;
}

export interface MessageReaction {
  userId: string;
  emoji: string;
  createdAt: string;
}

export interface Message {
  id: string;
  chatId: string;
  clientMessageId?: string | undefined;
  providerMessageId?: string | undefined;
  direction: 'inbound' | 'outbound';
  sender: 'contact' | 'agent' | 'ai';
  content: string;
  media?: MediaReference | undefined;
  status: 'pending' | 'received' | 'sent' | 'delivered' | 'read' | 'failed';
  createdAt: string;
  updatedAt: string;
  errorCode?: string | undefined;
  replyToMessageId?: string | undefined;
  reactions?: MessageReaction[] | undefined;
  pinned?: boolean | undefined;
  pinnedAt?: string | undefined;
}

export type ConversationType =
  | 'direct'
  | 'group'
  | 'community'
  | 'channel'
  | 'status'
  | 'broadcast'
  | 'unknown';

export interface ConversationCapability {
  type: ConversationType;
  supported: boolean;
  reason: string;
}

export interface Chat {
  id: string;
  ownerUserId: string;
  accountId: string;
  provider?: 'disabled' | 'whatsapp_cloud' | 'local_qr' | undefined;
  externalId?: string | undefined;
  participantId?: string | undefined;
  conversationType?: ConversationType | undefined;
  title: string;
  phone?: string | undefined;
  consent: 'unknown' | 'opted_in' | 'opted_out';
  consentUpdatedAt?: string | undefined;
  consentSource?: 'manual' | 'keyword' | 'provider' | undefined;
  lastInboundAt?: string | undefined;
  lastOutboundAt?: string | undefined;
  lastReadAt?: string | undefined;
  favorite?: boolean | undefined;
  automode: 'inherit' | 'on' | 'off';
  pausedUntil?: string | undefined;
  styleProfile?: string | undefined;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | undefined;
  lastMessage?: Message | null | undefined;
  unreadCount?: number | undefined;
}

export interface Settings {
  userId: string;
  globalAutomode: boolean;
  updatedAt: string;
}

export interface ConnectorStatus {
  provider: 'disabled' | 'whatsapp_cloud' | 'local_qr';
  state:
    | 'disconnected'
    | 'qr_required'
    | 'connecting'
    | 'authenticated'
    | 'ready'
    | 'reconnecting'
    | 'degraded'
    | 'error';
  configured: boolean;
  accountId?: string | undefined;
  reason?: string | undefined;
  qrDataUrl?: string | undefined;
  updatedAt: string;
}

export interface AIStatus {
  provider: 'disabled' | 'gemini' | 'nvidia' | 'openai' | 'anthropic' | 'perplexity';
  configured: boolean;
  state: 'ready' | 'degraded' | 'error';
  reason?: string | undefined;
  circuitOpenUntil?: string | undefined;
}

export interface AIProviderSummary {
  provider: string;
  configured: boolean;
  active: boolean;
  model: string | null;
  testStatus: 'untested' | 'passed' | 'failed' | null;
  testLatencyMs: number | null;
  testedAt: string | null;
  lastError: string | null;
}

export interface MediaGenerationSummary {
  provider: 'openai';
  configured: boolean;
  model: string;
  testStatus: 'untested' | 'passed' | 'failed';
  testLatencyMs: number | null;
  testedAt: string | null;
  lastError: string | null;
}

export interface SystemStatus {
  server: { status: 'ready' | 'not_ready'; version: string };
  whatsapp: ConnectorStatus;
  ai: AIStatus;
}

export type ApiResponse<T> =
  | {
      success: true;
      data: T;
      error: null;
      meta: { requestId: string; [key: string]: unknown };
    }
  | {
      success: false;
      data: null;
      error: { code: string; message: string; details?: unknown };
      meta: { requestId: string; [key: string]: unknown };
    };

export type AppErrorState = {
  code: string;
  message: string;
};

export type ChatUpdateInput = Partial<
  Pick<Chat, 'title' | 'consent' | 'automode' | 'ownerUserId' | 'lastReadAt' | 'favorite'>
> & {
  pausedUntil?: string | null | undefined;
  styleProfile?: string | null | undefined;
};

export const isWithinMessagingWindow = (chat: Chat): boolean => {
  if (!chat.lastInboundAt) return false;
  const timestamp = Date.parse(chat.lastInboundAt);
  return (
    Number.isFinite(timestamp) &&
    timestamp <= Date.now() &&
    Date.now() - timestamp <= 24 * 60 * 60 * 1_000
  );
};

export type AccountProvider = 'whatsapp_cloud' | 'local_qr';

export interface AccountSummary {
  id: string;
  label: string;
  provider: AccountProvider;
  isActive: boolean;
  status: ConnectorStatus;
  capabilities: ConversationCapability[];
}

export type RelationshipType = 'unknown' | 'vip' | 'client' | 'lead' | 'friend' | 'family';

export interface Contact {
  id: string;
  accountId: string;
  phone: string;
  name: string;
  relationshipType: RelationshipType;
  tags: string[];
  notes: string;
  blocked: boolean;
  optInMarketing: boolean;
  optInUpdates: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ToneProfile {
  formality: 'formal' | 'casual' | 'mixed';
  energy: 'high' | 'medium' | 'low';
  emojiUsage: 'none' | 'occasional' | 'frequent';
  avgMessageLength: 'short' | 'medium' | 'long';
  preferredGreeting?: string | undefined;
  preferredClosing?: string | undefined;
  punctuationStyle: 'strict' | 'loose' | 'none';
  emojiPreferences: string[];
}

export interface ContactMemory {
  contactId: string;
  language: string;
  toneProfile: ToneProfile;
  topicHistory: string[];
  importantFacts: string[];
  sampleMessages: string[];
  outgoingSamples: string[];
  conversationLog: { role: 'user' | 'assistant'; content: string; ts: string }[];
  conversationSummaries: string[];
  lastConversationTs?: string | undefined;
  lastStyleLearn?: string | undefined;
  responseTimes: { avgSeconds: number; samples: number[] };
  openTopics: string[];
  sentimentScore: number;
  messageCount: number;
  incomingCount: number;
  updatedAt: string;
}

export interface StyleProfile {
  userId: string;
  messagesAnalyzed: number;
  avgLength: number;
  emojiRatio: number;
  formalRatio: number;
  commonWords: string[];
  sampleMessages: string[];
  toneDescription: string;
  language: string;
  learningLevel: number;
  avgResponseSeconds: number;
  updatedAt: string;
}

export interface GlobalMemory {
  id: string;
  activeSummaries: { contactName: string; contactId: string; summary: string }[];
  recentTopics: string[];
  updatedAt: string;
}

export type CallStatus =
  | 'ringing'
  | 'queued'
  | 'accepted'
  | 'connecting'
  | 'connected'
  | 'on_hold'
  | 'transferred'
  | 'ended'
  | 'missed'
  | 'rejected'
  | 'cancelled'
  | 'failed';
export type CallRoute = 'vip_immediate' | 'auto_voicemail' | 'queue_normal';
export type CallEndReason =
  | 'normal'
  | 'caller_hangup'
  | 'operator_hangup'
  | 'ai_hangup'
  | 'timeout'
  | 'rejected'
  | 'missed'
  | 'cancelled'
  | 'failed'
  | 'transferred'
  | undefined;

export const ACTIVE_CALL_STATES: ReadonlySet<CallStatus> = new Set([
  'ringing',
  'queued',
  'accepted',
  'connecting',
  'connected',
  'on_hold',
  'transferred',
]);

export const TERMINAL_CALL_STATES: ReadonlySet<CallStatus> = new Set([
  'ended',
  'missed',
  'rejected',
  'cancelled',
  'failed',
]);

export interface CallRecord {
  id: string;
  accountId: string;
  providerCallId?: string | undefined;
  from: string;
  isVideo: boolean;
  isGroup: boolean;
  roomUrl?: string | undefined;
  status: CallStatus;
  priority: number;
  route: CallRoute;
  direction?: 'inbound' | 'outbound' | undefined;
  operatorUserId?: string | undefined;
  startedAt: string;
  answeredAt?: string | undefined;
  rejectedAt?: string | undefined;
  endedAt?: string | undefined;
  endReason?: CallEndReason;
  summary?: string | undefined;
  transcript?: string | undefined;
  transcriptSummary?: string | undefined;
  transferredTo?: string | undefined;
  transferReason?: string | undefined;
  transferredAt?: string | undefined;
  contactId?: string | undefined;
  createdAt: string;
  updatedAt: string;
}

export type AITask =
  | 'reply'
  | 'help'
  | 'followup'
  | 'extract'
  | 'summarize'
  | 'analyze'
  | 'tone'
  | 'style'
  | 'suggest'
  | 'safety'
  | 'translate'
  | 'ad_copy';

export interface AITaskResult {
  task: AITask;
  model: string;
  result: { content: string; safe: boolean };
}

export type AITaskErrorCode =
  | 'AI_PROVIDER_NOT_CONFIGURED'
  | 'AI_PROVIDER_AUTH_ERROR'
  | 'AI_PROVIDER_RATE_LIMIT'
  | 'AI_PROVIDER_TIMEOUT'
  | 'AI_PROVIDER_INVALID_RESPONSE'
  | 'AI_PROVIDER_NETWORK_ERROR'
  | 'AI_PROVIDER_UNAVAILABLE'
  | 'AI_PROVIDER_FAILURE';

export interface NvidiaTaskSummary {
  slot: 'learning' | 'ads';
  label: string;
  configured: boolean;
  model: string;
  testStatus: 'untested' | 'passed' | 'failed';
  testLatencyMs: number | null;
  testedAt: string | null;
  lastError: string | null;
}

export interface AvatarAgentStatus {
  callId: string;
  state: 'stopped' | 'starting' | 'running' | 'error' | 'takeover';
  startedAt: string;
  stoppedAt?: string | undefined;
  transcriptSegments: number;
  summary?: string | undefined;
}

export interface LinkedDevice {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  status: 'active' | 'revoked';
  pairedAt: string;
  lastActiveAt: string;
  lastKnownIp?: string | undefined;
  isCurrent: boolean;
}

export interface QuickReply {
  id: string;
  ownerUserId: string;
  shortcut: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledMessage {
  id: string;
  ownerUserId: string;
  chatId: string;
  content: string;
  scheduledAt: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  sentAt?: string | undefined;
  error?: string | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface Reminder {
  id: string;
  ownerUserId: string;
  chatId: string;
  title: string;
  remindAt: string;
  status: 'pending' | 'fired' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface MediaRecord {
  id: string;
  chatId: string;
  messageId: string;
  type: 'audio' | 'document' | 'image' | 'sticker' | 'video' | 'unknown';
  mimeType: string;
  filename: string;
  localPath: string;
  size: number;
  checksum: string;
  direction: 'inbound' | 'outbound';
  createdAt: string;
}

// Voice Agent types
export interface VoiceSettings {
  userId: string;
  voiceAutomode: boolean;
  killSwitch: boolean;
  businessHoursOnly: boolean;
  maxCallDurationMin: number;
  businessName: string;
  toneOfVoice: string;
  faqSummary: string;
  languagePreference: string;
  updatedAt: string;
}

export type VoiceConsentPurpose =
  | 'create_voice_clone'
  | 'ai_answer_inbound_calls'
  | 'store_biometric_voice_sample'
  | 'share_with_tts_processor'
  | 'learning_from_call';

export interface VoiceConsent {
  id: string;
  userId: string;
  version: string;
  acceptedAt: string;
  purposes: VoiceConsentPurpose[];
  retentionDays: number;
}

export interface VoiceSampleMeta {
  id: string;
  phraseId: string;
  durationMs: number;
  sampleRate: number;
  storageKey: string;
  createdAt: string;
}

export interface VoiceProfile {
  id: string;
  userId: string;
  status: 'pending_enrollment' | 'processing' | 'active' | 'failed' | 'deleted';
  version: number;
  languagePrimary: string;
  provider: string;
  consentId: string;
  qualityTier: 'instant' | 'professional';
  createdAt: string;
  updatedAt: string;
  consentAcceptedAt?: string | undefined;
}

export interface VoiceCallSession {
  id: string;
  businessId: string;
  direction: 'inbound' | 'outbound' | 'test';
  transport: 'webrtc_app' | 'whatsapp_calling' | 'sip_pstn';
  status: 'ringing' | 'ai_active' | 'human_active' | 'ended' | 'failed' | 'rejected_by_policy';
  answeredBy: 'ai' | 'human' | 'voicemail' | 'none';
  language: string;
  startedAt: string;
  endedAt?: string;
  disclosureSpoken: boolean;
}

// WhatsApp Templates
export type TemplateCategory = 'marketing' | 'utility' | 'authentication';
export type TemplateStatus = 'pending' | 'approved' | 'rejected' | 'paused' | 'draft';

export interface TemplateComponent {
  type: 'header' | 'body' | 'footer' | 'buttons';
  format?: string;
  text?: string;
  buttons?: Array<{
    type: 'quick_reply' | 'url' | 'phone_number' | 'flow';
    text: string;
    url?: string;
    phone_number?: string;
    flow_id?: string;
  }>;
}

export interface WhatsAppTemplate {
  id: string;
  ownerUserId: string;
  wabaTemplateId?: string;
  name: string;
  category: TemplateCategory;
  language: string;
  status: TemplateStatus;
  components: TemplateComponent[];
  createdAt: string;
  updatedAt: string;
}

// Social Marketing / Ads types
export type AdPlatform = 'whatsapp' | 'facebook' | 'instagram' | 'x' | 'tiktok' | 'youtube' | 'linkedin' | 'ads';
export type AdStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';
export type AdContentType = 'text' | 'image' | 'video' | 'carousel' | 'reel';

export interface AdTemplate {
  type: string;
  emoji: string;
  title: string;
  content: string;
  img?: string;
}

export interface AdPlatformSpec {
  name: string;
  icon: string;
  color: string;
  maxChars: number;
  media: boolean;
  specs: Record<string, string>;
  seoTips: string[];
  templates: AdTemplate[];
}

export interface AdRecord {
  id: string;
  ownerUserId: string;
  title: string;
  content: string;
  contentType: AdContentType;
  mediaUrls: string[];
  platforms: AdPlatform[];
  hashtags: string;
  linkUrl: string;
  whatsappPhone?: string;
  status: AdStatus;
  scheduledAt?: string;
  publishedAt?: string;
  error?: string;
  clicks: number;
  conversions: number;
  aiGenerated: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdClickRecord {
  id: string;
  adId: string;
  ipHash: string;
  userAgent?: string;
  platform?: string;
  clickedAt: string;
  convertedToWhatsApp: boolean;
}

export interface SocialAccount {
  id: string;
  ownerUserId: string;
  platform: AdPlatform;
  accountName: string;
  accountHandle: string;
  accountAvatar?: string;
  status: 'active' | 'disconnected' | 'error';
  connectedAt: string;
  updatedAt: string;
}

export interface AdAutomation {
  id: string;
  ownerUserId: string;
  name: string;
  type: 'ai_auto' | 'rss_auto' | 'schedule_repeat';
  platforms: AdPlatform[];
  accountIds: string[];
  frequency: string;
  timeSlot: string;
  prompt: string;
  tone: string;
  isActive: boolean;
  runCount: number;
  lastRunAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SocialSettings {
  userId: string;
  defaultTone: string;
  defaultLanguage: string;
  autoHashtags: boolean;
  shortLinks: boolean;
  updatedAt: string;
}

// --- ATA (geheugengraaf) ---

export type AtaEntityType = 'person' | 'place' | 'time' | 'event' | 'topic';
export type AtaPredicate = 'meets_at' | 'agreed_with' | 'located_at' | 'related_to' | 'prefers' | 'dislikes';
export type AtaInvalidationReason =
  | 'consistency_conflict'
  | 'user_invalidated'
  | 'extraction_correction';

export interface AtaEntity {
  id: string;
  accountId: string;
  type: AtaEntityType;
  name: string;
  aliases: string[];
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface AtaRelation {
  id: string;
  accountId: string;
  subjectEntityId: string;
  predicate: AtaPredicate;
  objectEntityId: string;
  sourceContactId: string;
  sourceMessageId: string;
  validFrom: string;
  validUntil: string | null;
  confidence: number;
  createdAt: string;
  invalidationReason?: AtaInvalidationReason;
}

export interface AtaMemoryResponse {
  entities: AtaEntity[];
  relations: AtaRelation[];
  truncated: boolean;
}

export interface AtaHealthReport {
  id: string;
  accountId: string;
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  conflictingEntities: {
    entityId: string;
    entityName: string;
    conflictCount: number;
  }[];
  uncertainContacts: {
    contactId: string;
    unresolvedOutcomeCount: number;
  }[];
  predicateDistribution: {
    predicate: string;
    count: number;
  }[];
  summary: string;
}

export interface AtaHealthReportsResponse {
  reports: AtaHealthReport[];
}

// --- Fase 8: Sensitieve-categorie consent ---

export type AtaSensitiveCategory =
  | 'health'
  | 'religion'
  | 'political'
  | 'sexual_orientation'
  | 'adult_content'
  | 'other_sensitive';

export interface AtaSensitiveConsentRecord {
  id: string;
  accountId: string;
  contactId: string;
  category: AtaSensitiveCategory;
  status: 'granted' | 'revoked' | 'not_asked';
  grantedAt: string | null;
  revokedAt: string | null;
  source: 'explicit_message' | 'ui_toggle';
}

export interface AccountSensitiveDefaults {
  accountId: string;
  categoryEnabledByDefault: Record<AtaSensitiveCategory, boolean | 'not_configured'>;
}

export interface AtaSensitiveConsentResponse {
  consents: AtaSensitiveConsentRecord[];
  defaults: AccountSensitiveDefaults;
}

// --- Fase 8: Predicate-voorstellen ---

export interface AtaPredicateProposal {
  id: string;
  accountId: string;
  predicateName: string;
  exampleTexts: string[];
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: 'pending' | 'approved' | 'rejected';
  isSensitive: boolean;
  sensitiveCategory: AtaSensitiveCategory | null;
  trustLevel: 'new' | 'high_trust';
  reviewedAt?: string;
  reviewedBy?: string;
}

export interface AtaPredicateProposalsResponse {
  proposals: AtaPredicateProposal[];
}

// --- Fase 8: Zelf-tuning ---

export interface AtaTuningSettings {
  accountId: string;
  confidenceThresholds: Partial<Record<string, number>>;
  maxRelationsInPrompt: number;
  correctionConfidenceBoost: number;
  decayRatePerDay: number;
  contactToneDefaults: Record<string, 'formal' | 'casual' | 'neutral'>;
  updatedAt: string;
}

export interface AtaTuningLogRecord {
  id: string;
  accountId: string;
  parameter: string;
  oldValue: string;
  newValue: string;
  reason: string;
  dataBasis: string;
  createdAt: string;
  supersededBy?: string;
}

export interface AtaTuningResponse {
  settings: AtaTuningSettings;
  logs: AtaTuningLogRecord[];
}

export interface AtaTuningRollbackResponse {
  success: boolean;
  warning?: string;
}